/**
 * PAY-UNIFIED-02: POS Reconciliation Workspace
 *
 * RBAC: finance.payment.cashier_collect / finance.payment.reconcile / finance.admin.manage
 * Backend: /api/finance/pos/* (routes_pos.py)
 */

import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  CreditCard,
  Plus,
  RefreshCw,
  CheckCircle,
  Settings,
  Layers,
  ToggleLeft,
  ToggleRight,
  ListChecks,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => v != null ? `₹${parseFloat(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—";

const STATUS_COLORS = {
  REPORTED:              "bg-amber-100 text-amber-800",
  VERIFIED:              "bg-emerald-100 text-emerald-800",
  SETTLED:               "bg-blue-100 text-blue-800",
  EXCEPTION:             "bg-red-100 text-red-800",
  REVERSED:              "bg-gray-100 text-gray-600",
  PENDING_VERIFICATION:  "bg-amber-100 text-amber-800",
  RECONCILED:            "bg-emerald-100 text-emerald-800",
  CLOSED:                "bg-gray-100 text-gray-600",
};

const StatusBadge = ({ s }) => (
  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[s] || "bg-gray-100 text-gray-600"}`}>
    {s?.replace(/_/g, " ")}
  </span>
);

// ─── Register Terminal Dialog ────────────────────────────────────────────────
function RegisterTerminalDialog({ token, onRegistered, onClose }) {
  const [form, setForm] = useState({ terminal_identifier: "", acquirer_name: "", merchant_account_reference: "", location: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.terminal_identifier || !form.acquirer_name) {
      toast.error("Terminal ID and Acquirer Name are required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/finance/pos/terminals`, {
        terminal_identifier: form.terminal_identifier,
        acquirer_name: form.acquirer_name,
        merchant_account_reference: form.merchant_account_reference || undefined,
        location: form.location || undefined,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("POS terminal registered");
      onRegistered();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Registration failed");
    } finally {
      setSaving(false);
    }
  };

  const f = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div className="space-y-4 py-1">
      <div>
        <Label>Terminal Identifier <span className="text-red-500">*</span></Label>
        <Input data-testid="terminal-id-input" value={form.terminal_identifier} onChange={(e) => f("terminal_identifier", e.target.value)} placeholder="e.g. TID-0001" />
      </div>
      <div>
        <Label>Acquirer Name <span className="text-red-500">*</span></Label>
        <Input data-testid="terminal-acquirer-input" value={form.acquirer_name} onChange={(e) => f("acquirer_name", e.target.value)} placeholder="e.g. HDFC Bank POS" />
      </div>
      <div>
        <Label>Merchant Account Reference</Label>
        <Input data-testid="terminal-merchant-ref-input" value={form.merchant_account_reference} onChange={(e) => f("merchant_account_reference", e.target.value)} placeholder="MID-XXXXXXXX" />
      </div>
      <div>
        <Label>Location</Label>
        <Input data-testid="terminal-location-input" value={form.location} onChange={(e) => f("location", e.target.value)} placeholder="e.g. Main Gate Counter" />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} size="sm">Cancel</Button>
        <Button data-testid="register-terminal-submit-btn" onClick={submit} disabled={saving} size="sm">
          {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
          {saving ? "Registering…" : "Register Terminal"}
        </Button>
      </div>
    </div>
  );
}

// ─── Record POS Transaction Dialog ───────────────────────────────────────────
function RecordTransactionDialog({ token, terminals, onRecorded, onClose }) {
  const [currencies, setCurrencies] = useState([]);
  const [form, setForm] = useState({
    terminal_id: "",
    acquirer_reference: "",
    card_network: "VISA",
    transaction_date: new Date().toISOString().slice(0, 10),
    amount: "",
    currency_id: "",
    payment_id: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/finance/admin/currencies`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const list = r.data?.items || r.data?.currencies || [];
        setCurrencies(list);
        if (list.length > 0) setForm((p) => ({ ...p, currency_id: list[0].id }));
      })
      .catch(() => {});
  }, [token]);

  const submit = async () => {
    if (!form.terminal_id || !form.acquirer_reference || !form.amount || !form.currency_id) {
      toast.error("Terminal, Acquirer Reference, Amount, and Currency are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        terminal_id: form.terminal_id,
        acquirer_reference: form.acquirer_reference,
        card_network: form.card_network || null,
        transaction_date: form.transaction_date,
        amount: parseFloat(form.amount),
        currency_id: form.currency_id,
        payment_id: form.payment_id || null,
        notes: form.notes || null,
      };
      await axios.post(`${API}/api/finance/pos/transactions`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("POS transaction recorded (Status: REPORTED)");
      onRecorded();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === "object" ? detail?.message : detail) || "Failed to record transaction");
    } finally {
      setSaving(false);
    }
  };

  const f = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div className="space-y-3 py-1 max-h-[70vh] overflow-y-auto">
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800">
        INV-POS-02: REPORTED status does NOT certify settlement. Only batch reconciliation settles POS transactions.
      </div>
      <div>
        <Label>Terminal <span className="text-red-500">*</span></Label>
        <Select value={form.terminal_id} onValueChange={(v) => f("terminal_id", v)}>
          <SelectTrigger data-testid="pos-txn-terminal-select"><SelectValue placeholder="Select terminal" /></SelectTrigger>
          <SelectContent>
            {terminals.filter((t) => t.is_active).map((t) => (
              <SelectItem key={t.id} value={t.id}>{t.terminal_identifier} — {t.acquirer_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Acquirer Reference <span className="text-red-500">*</span></Label>
        <Input data-testid="pos-txn-acquirer-ref-input" value={form.acquirer_reference} onChange={(e) => f("acquirer_reference", e.target.value)} placeholder="e.g. HDFC-POS-20241031-123456" />
        <p className="text-xs text-gray-500 mt-0.5">The reference printed on the POS receipt by the acquiring bank</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Card Network</Label>
          <Select value={form.card_network} onValueChange={(v) => f("card_network", v)}>
            <SelectTrigger data-testid="pos-txn-network-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["VISA", "MASTERCARD", "RUPAY", "AMEX", "UNKNOWN"].map((n) => (
                <SelectItem key={n} value={n}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Transaction Date</Label>
          <Input type="date" data-testid="pos-txn-date-input" value={form.transaction_date} onChange={(e) => f("transaction_date", e.target.value)} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Amount <span className="text-red-500">*</span></Label>
          <Input type="number" data-testid="pos-txn-amount-input" value={form.amount} onChange={(e) => f("amount", e.target.value)} min="0.01" step="0.01" />
        </div>
        <div>
          <Label>Currency <span className="text-red-500">*</span></Label>
          <Select value={form.currency_id} onValueChange={(v) => f("currency_id", v)}>
            <SelectTrigger data-testid="pos-txn-currency-select"><SelectValue placeholder="Select currency" /></SelectTrigger>
            <SelectContent>
              {currencies.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.iso_code} — {c.name || c.iso_code}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Linked Payment ID (optional)</Label>
        <Input data-testid="pos-txn-paymentid-input" value={form.payment_id} onChange={(e) => f("payment_id", e.target.value)} placeholder="UUID of cashier payment (optional)" />
      </div>
      <div>
        <Label>Notes (optional)</Label>
        <Input data-testid="pos-txn-notes-input" value={form.notes} onChange={(e) => f("notes", e.target.value)} placeholder="e.g. Student paid via RUPAY at gate" />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} size="sm">Cancel</Button>
        <Button data-testid="pos-txn-submit-btn" onClick={submit} disabled={saving} size="sm">
          {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <CreditCard className="h-4 w-4 mr-1" />}
          {saving ? "Recording…" : "Record Transaction"}
        </Button>
      </div>
    </div>
  );
}

// ─── Open Settlement Batch Dialog ────────────────────────────────────────────
function OpenSettlementBatchDialog({ token, terminals, onOpened, onClose }) {
  const [terminalId, setTerminalId] = useState("");
  const [batchDate, setBatchDate] = useState(new Date().toISOString().slice(0, 10));
  const [acquirerRef, setAcquirerRef] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!terminalId) { toast.error("Select a terminal"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/finance/pos/batches`, {
        terminal_id: terminalId,
        batch_date: batchDate,
        acquirer_batch_reference: acquirerRef || undefined,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Settlement batch created");
      onOpened();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === "object" ? detail?.message : detail) || "Failed to create batch");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 py-1">
      <div>
        <Label>Terminal <span className="text-red-500">*</span></Label>
        <Select value={terminalId} onValueChange={setTerminalId}>
          <SelectTrigger data-testid="batch-terminal-select"><SelectValue placeholder="Select terminal" /></SelectTrigger>
          <SelectContent>
            {terminals.filter((t) => t.is_active).map((t) => (
              <SelectItem key={t.id} value={t.id}>{t.terminal_identifier} — {t.acquirer_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Batch Date <span className="text-red-500">*</span></Label>
        <Input type="date" data-testid="batch-date-input" value={batchDate} onChange={(e) => setBatchDate(e.target.value)} />
      </div>
      <div>
        <Label>Acquirer Batch Reference (optional)</Label>
        <Input data-testid="batch-acquirer-ref-input" value={acquirerRef} onChange={(e) => setAcquirerRef(e.target.value)} placeholder="e.g. HDFC-BATCH-20241031" />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} size="sm">Cancel</Button>
        <Button data-testid="batch-open-submit-btn" onClick={submit} disabled={saving} size="sm">
          {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <Layers className="h-4 w-4 mr-1" />}
          {saving ? "Creating…" : "Create Batch"}
        </Button>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function POSReconWorkspace() {
  const { user, token } = useAuth();
  const [tab, setTab] = useState("terminals");
  const [terminals, setTerminals] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null);

  const isAdmin = ["finance_manager", "institution_admin", "super_admin"].includes(user?.role);
  const isManager = ["finance_manager", "cfo", "institution_admin", "super_admin"].includes(user?.role);

  const loadTerminals = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/finance/pos/terminals`, { headers: { Authorization: `Bearer ${token}` } });
      setTerminals(r.data.terminals || []);
    } catch { toast.error("Failed to load POS terminals"); }
  }, [token]);

  const loadTransactions = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/finance/pos/transactions?limit=50`, { headers: { Authorization: `Bearer ${token}` } });
      setTransactions(r.data.transactions || []);
    } catch { toast.error("Failed to load POS transactions"); }
  }, [token]);

  const loadBatches = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/finance/pos/batches?limit=30`, { headers: { Authorization: `Bearer ${token}` } });
      setBatches(r.data.batches || []);
    } catch { toast.error("Failed to load settlement batches"); }
  }, [token]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([loadTerminals(), loadTransactions(), loadBatches()]).finally(() => setLoading(false));
  }, [loadTerminals, loadTransactions, loadBatches]);

  useEffect(() => { load(); }, [load]);

  const toggleTerminal = async (terminal) => {
    try {
      const newState = !terminal.is_active;
      await axios.patch(
        `${API}/api/finance/pos/terminals/${terminal.id}/status?is_active=${newState}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Terminal ${newState ? "activated" : "deactivated"}`);
      loadTerminals();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === "object" ? detail?.message : detail) || "Failed to update terminal");
    }
  };

  const closeBatch = async (batchId) => {
    try {
      const r = await axios.patch(
        `${API}/api/finance/pos/batches/${batchId}/reconcile`,
        { notes: "Manual reconcile from POS Recon Workspace" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Batch reconciled. ${r.data.matched_transaction_count || 0} txns settled, ${r.data.exceptions_created || 0} exceptions.`);
      loadBatches();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === "object" ? detail?.message : detail) || "Failed to reconcile batch");
    }
  };

  const verifyTxn = async (txnId) => {
    try {
      await axios.patch(`${API}/api/finance/pos/transactions/${txnId}/verify`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Transaction verified");
      loadTransactions();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === "object" ? detail?.message : detail) || "Failed to verify");
    }
  };

  const pendingCount = transactions.filter((t) => t.status === "REPORTED").length;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="pos-recon-workspace">
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-xl font-semibold text-gray-900">POS Reconciliation</p>
              <p className="text-sm text-gray-500">Terminal registry · Card transactions · Settlement batches · PAY-02</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={load} data-testid="pos-refresh-btn">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            {isAdmin && (
              <Button size="sm" variant="outline" data-testid="register-terminal-btn" onClick={() => setDialog("register")}>
                <Plus className="h-4 w-4 mr-1" />Register Terminal
              </Button>
            )}
            <Button size="sm" data-testid="record-pos-txn-btn" onClick={() => setDialog("record-txn")}>
              <CreditCard className="h-4 w-4 mr-1" />Record Transaction
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white border-b px-6">
        <div className="flex gap-0">
          {[
            { key: "terminals",    label: "Terminals",          icon: Settings, testid: "pos-tab-terminals" },
            { key: "transactions", label: "Transactions",       icon: CreditCard, testid: "pos-tab-transactions", badge: pendingCount || null },
            { key: "batches",      label: "Settlement Batches", icon: Layers,   testid: "pos-tab-batches" },
          ].map((t) => (
            <button
              key={t.key}
              data-testid={t.testid}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t.key ? "border-indigo-600 text-indigo-700" : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
              {t.badge ? <span className="rounded-full bg-amber-100 text-amber-800 text-xs px-1.5 py-0.5 font-semibold">{t.badge}</span> : null}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6 space-y-4">
        {tab === "terminals" && (
          <div data-testid="pos-terminals-panel">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-gray-800">Registered POS Terminals</h2>
              <span className="text-xs text-gray-500">{terminals.length} terminal{terminals.length !== 1 ? "s" : ""}</span>
            </div>
            {terminals.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  <CreditCard className="h-8 w-8 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No terminals registered</p>
                  <p className="text-sm mt-1">Register your first POS terminal to begin tracking card transactions.</p>
                  {isAdmin && <Button className="mt-4" size="sm" onClick={() => setDialog("register")} data-testid="register-terminal-empty-btn"><Plus className="h-4 w-4 mr-1" />Register Terminal</Button>}
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {terminals.map((t) => (
                  <Card key={t.id} data-testid={`terminal-card-${t.id}`} className={`border ${t.is_active ? "border-emerald-200" : "border-gray-200 opacity-70"}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="font-semibold text-sm">{t.terminal_identifier}</div>
                          <div className="text-xs text-gray-500">{t.acquirer_name}</div>
                        </div>
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${t.is_active ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-600"}`}>
                          {t.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>
                      {t.location && <div className="text-xs text-gray-500 mb-1">📍 {t.location}</div>}
                      {t.merchant_account_reference && <div className="text-xs text-gray-400 font-mono mb-2">{t.merchant_account_reference}</div>}
                      {isAdmin && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full text-xs h-7 mt-1"
                          data-testid={`toggle-terminal-${t.id}`}
                          onClick={() => toggleTerminal(t)}
                        >
                          {t.is_active ? <><ToggleLeft className="h-3 w-3 mr-1" />Deactivate</> : <><ToggleRight className="h-3 w-3 mr-1" />Activate</>}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "transactions" && (
          <div data-testid="pos-transactions-panel">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-gray-800">POS Card Transactions</h2>
              <Button size="sm" variant="outline" onClick={() => setDialog("record-txn")} data-testid="record-txn-header-btn">
                <Plus className="h-4 w-4 mr-1" />Record
              </Button>
            </div>
            {transactions.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-gray-500"><CreditCard className="h-8 w-8 mx-auto mb-3 text-gray-300" /><p className="font-medium">No POS transactions yet</p></CardContent></Card>
            ) : (
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Terminal</TableHead>
                      <TableHead>Acquirer Reference</TableHead>
                      <TableHead>Network</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((txn) => (
                      <TableRow key={txn.id} data-testid={`pos-txn-row-${txn.id}`}>
                        <TableCell className="text-sm">{txn.transaction_date}</TableCell>
                        <TableCell className="text-xs font-mono">{txn.terminal_identifier || "—"}</TableCell>
                        <TableCell className="text-xs font-mono text-blue-600 max-w-[150px] truncate">{txn.acquirer_reference}</TableCell>
                        <TableCell className="text-xs">{txn.card_network || "—"}</TableCell>
                        <TableCell className="text-right font-semibold text-sm">{fmt(txn.amount)}</TableCell>
                        <TableCell><StatusBadge s={txn.status} /></TableCell>
                        <TableCell>
                          {txn.status === "REPORTED" && isManager && (
                            <Button size="sm" variant="outline" className="text-xs h-7 border-emerald-300 text-emerald-700" data-testid={`verify-txn-btn-${txn.id}`} onClick={() => verifyTxn(txn.id)}>
                              <CheckCircle className="h-3 w-3 mr-1" />Verify
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </div>
        )}

        {tab === "batches" && (
          <div data-testid="pos-batches-panel">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-gray-800">Settlement Batches</h2>
              {isManager && (
                <Button size="sm" variant="outline" onClick={() => setDialog("open-batch")} data-testid="open-batch-btn">
                  <Layers className="h-4 w-4 mr-1" />New Batch
                </Button>
              )}
            </div>
            {batches.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-gray-500"><Layers className="h-8 w-8 mx-auto mb-3 text-gray-300" /><p className="font-medium">No settlement batches</p></CardContent></Card>
            ) : (
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Terminal</TableHead>
                      <TableHead>Batch Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Expected Total</TableHead>
                      <TableHead className="text-right">Matched Total</TableHead>
                      <TableHead className="text-right">Matched Txns</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {batches.map((b) => (
                      <TableRow key={b.id} data-testid={`batch-row-${b.id}`}>
                        <TableCell className="text-xs font-mono">{b.terminal_identifier || "—"}</TableCell>
                        <TableCell className="text-sm">{b.batch_date}</TableCell>
                        <TableCell><StatusBadge s={b.status} /></TableCell>
                        <TableCell className="text-right">{fmt(b.expected_total_amount)}</TableCell>
                        <TableCell className="text-right font-semibold">{fmt(b.matched_total_amount)}</TableCell>
                        <TableCell className="text-right">
                          <span className="rounded-full bg-gray-100 text-gray-700 text-xs px-2">{b.matched_transaction_count || 0}</span>
                        </TableCell>
                        <TableCell>
                          {(b.status === "PENDING_VERIFICATION" || b.status === "VERIFIED") && isManager && (
                            <Button size="sm" variant="outline" className="text-xs h-7 border-blue-300 text-blue-700" data-testid={`reconcile-batch-btn-${b.id}`} onClick={() => closeBatch(b.id)}>
                              <ListChecks className="h-3 w-3 mr-1" />Reconcile
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </div>
        )}
      </div>

      <Dialog open={dialog === "register"} onOpenChange={() => setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Register POS Terminal</DialogTitle></DialogHeader>
          <RegisterTerminalDialog token={token} onRegistered={() => { loadTerminals(); setDialog(null); }} onClose={() => setDialog(null)} />
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "record-txn"} onOpenChange={() => setDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5" />Record POS Transaction</DialogTitle></DialogHeader>
          <RecordTransactionDialog
            token={token}
            terminals={terminals}
            onRecorded={() => { loadTransactions(); setDialog(null); }}
            onClose={() => setDialog(null)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "open-batch"} onOpenChange={() => setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Layers className="h-5 w-5" />Create Settlement Batch</DialogTitle></DialogHeader>
          <OpenSettlementBatchDialog
            token={token}
            terminals={terminals}
            onOpened={() => { loadBatches(); setDialog(null); }}
            onClose={() => setDialog(null)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
